import { Component, ElementRef, OnInit, ViewChild } from '@angular/core'
import { File, Product, Vat } from 'depoto-core/src/entities'
import { CoreService, MdClassOption, NotificationService, ProductMdClassService } from '../../../services'
import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import { ProductDetailService } from '../../../services/product-detail.service'
import 'jsbarcode'
import { ProductCreatingService } from '../../../services/product-creating.service'
import { TranslateService } from '@ngx-translate/core'
import { getEndpoint } from 'depoto-core/src/utils/ApiUtils'
import { OAuthSession } from 'depoto-core/src/models'
import { getThumbUrl as imageGetThumbUrl } from '../../../utils'

@Component({
  selector: 'product-form',
  templateUrl: './product-form.component.html',
  styleUrls: ['product-form.component.css'],
})
export class ProductFormComponent implements OnInit {
  @ViewChild('barcode', { static: false }) barcode: ElementRef
  @ViewChild('printable') printableEl: ElementRef
  ckeditor = ClassicEditor
  vats: Vat[] = []
  mdClassOptions: MdClassOption[] = []

  constructor(
    private core: CoreService,
    private notificationService: NotificationService,
    public translateService: TranslateService,
    public productDetailService: ProductDetailService,
    public productCreatingService: ProductCreatingService,
    public productMdClassService: ProductMdClassService,
  ) {
    this.getVats()
    this.mdClassOptions = this.productMdClassService.getOptions()
  }

  ngOnInit() {
    this.mdClassOptions = this.productMdClassService.getOptions()
    if (this.product) {
      this.productDetailService.initMdClass()
    }
  }

  onMdClassChange(value: string) {
    this.productDetailService.selectedMdClass = value
  }

  get product() {
    return this.productDetailService.product
  }

  get depots() {
    return this.productDetailService.depots
  }

  get suppliers() {
    return this.productDetailService.suppliers
  }

  getChildren() {
    return [
      ...this.product.children,
      ...this.productCreatingService.childrenWithImagesToCreate.map(childrenAndImage => childrenAndImage.childToCreate),
    ]
  }

  public async fillNextEan() {
    this.productDetailService.product.ean = await this.core.services.product.getNextEan()
  }

  public async updateVariants(properties: string[]) {
    this.setLoading(true)
    try {
      const productPart: any = {}
      properties.forEach(p => {
        if (p === 'supplier') {
          productPart.supplier = this.productDetailService.product.supplier
        } else if (p === 'purchaseCurrency') {
          productPart.purchaseCurrency = this.productDetailService.product.purchaseCurrency
        } else {
          productPart[p] = this.productDetailService.product[p]
        }
      })
      await this.core.services.product.updateProductPart(
        { id: this.productDetailService.product.id, ...productPart },
        { id: null },
      )
      const promises = this.productDetailService.product.children.map(c =>
        this.core.services.product.updateProductPart({ id: c.id, ...productPart }, { id: null }),
      )
      await Promise.all(promises)
      this.notificationService.success(this.translateService.instant('alert.product.saved'))
    } catch (e) {
      console.log(e)
      this.notificationService.warning(this.translateService.instant('product-form.error', { error: e.message }))
    }
    this.setLoading(false)
  }

  public setParent(parent: Product): void {
    this.productDetailService.product.parent = parent
  }

  public addChild(child: Product): void {
    this.productDetailService.product.children.push(child)
  }

  public removeChild(child: Product): void {
    this.productDetailService.product.children.forEach((c, i) => {
      if (child.id === c.id) {
        this.productDetailService.product.children.splice(i, 1)
      }
    })
  }

  setLoading(val: boolean) {
    this.productDetailService.loading = val
  }

  openBarcode() {
    const text = encodeURIComponent(this.product.name)
    const origin = this.getBasePath()
    const url = `${origin}/barcode/${this.product.ean}?text=${text}`
    window.open(url, '_blank')
  }

  private getBasePath(): string {
    const endpointType: string = this.core.services.storage.getSync(this.core.services.storage.keys.auth.clientType)
    const session: OAuthSession = { ...new OAuthSession(), clientType: endpointType }
    return new URL(getEndpoint('', session)).origin
  }

  getThumbUrl(image: File): string {
    return imageGetThumbUrl(image)
  }

  get vat() {
    if (this.productDetailService.product?.vat?.id) {
      return this.productDetailService.product?.vat?.id
    } else {
      this.core.services.vat
        .getDefaultVat()
        .then(r => (this.productDetailService.product.vat = this.core.services.vat.defaultVat))
      // todo: it could happen, that there is no product set on pServ., in case user navigated away. there still be an error:
      // https://tomatom.sentry.io/issues/6910313452/?project=4506237783834624
      return this.productDetailService.product.vat.id
    }
  }

  get loading() {
    return this.productDetailService.loading
  }

  get submitted() {
    return this.productDetailService.submitted
  }

  get isCreating() {
    return this.productDetailService.isCreating
  }

  getVats() {
    this.vats = this.productDetailService.vats
  }
}
